import type { CatalogMuscleSlug } from "./muscleCatalog.js";

// Muscle-adjacency warnings — the API-level signal a human coach reads
// off a calendar at a glance but an LLM coach can't infer from a prompt:
// "this program trains the same muscle on two back-to-back days".
//
// Antenore 2026-06-22: a program is a CYCLE with no weekday binding —
// day 5 often lands on Sunday, so day 1 (Monday) is adjacent to it. A
// muscle hit on day 5 AND day 1 (e.g. incline press → pectoralis) is
// trained on consecutive calendar days, with too little rest for HIS
// body (distal tendons, triceps, inguinal hernia). The API does NOT
// judge whether the rest is enough — it surfaces every adjacency and the
// coach (human or LLM) decides. WARN-ONLY, never blocking — same idiom
// as programValidation.ts.
//
// This core is generic over an ordered DAY sequence so one function
// serves both consumers: the cyclic program template (programMuscleAdjacency)
// and the linear logged-session stream (sessionMuscleAdjacency). The
// `position` axis is whatever the distance is measured in — an ordinal
// session index for the cyclic path, an epoch-day for the calendar path —
// so "adjacent" is uniformly `distance === 1`.

// One day in the training sequence: the muscles it stimulates and where
// it sits on the distance axis.
export interface AdjacencyDay {
  /** Stable id (program-day key, session id, date). */
  key: string;
  /** Human label for the message + structured output (e.g. "Day 5"). */
  label: string;
  /** Distance axis: cyclic ordinal index (program) or epoch-day (logged). */
  position: number;
  /** Muscles trained that day (muscleContrib > 0), de-duplicated. */
  muscles: ReadonlySet<CatalogMuscleSlug>;
}

export interface AdjacencyOccurrence {
  key: string;
  label: string;
  position: number;
}

export interface AdjacencyPair {
  /** Day key of the earlier occurrence. */
  a: string;
  /** Day key of the later (or wrap-target) occurrence. */
  b: string;
  /** Distance in `position` units; 1 = adjacent calendar days. */
  distance: number;
}

export interface MuscleAdjacencyWarning {
  muscleSlug: CatalogMuscleSlug;
  /** Every day the muscle is trained, in schedule order (the full picture). */
  days: AdjacencyOccurrence[];
  /** Smallest gap between consecutive occurrences (cyclic wrap included). */
  minDistance: number;
  /** Consecutive-occurrence pairs at distance 1 — the actual adjacency. */
  adjacentPairs: AdjacencyPair[];
  /** English, LLM-readable summary. */
  message: string;
}

export interface AdjacencyOptions {
  /** When true the sequence wraps: the last day is adjacent to the first
   *  (a program repeats — day 5 Sunday → day 1 Monday of next cycle). */
  cyclic: boolean;
  /** Positions in one full cycle, used to measure the wrap distance. When
   *  omitted on a cyclic call it defaults to days.length (the common
   *  case: one position per day). */
  cycleLength?: number;
}

const buildMessage = (
  muscle: CatalogMuscleSlug,
  days: AdjacencyOccurrence[],
  adjacentPairs: AdjacencyPair[],
  cyclic: boolean,
): string => {
  const labelByKey = new Map(days.map((d) => [d.key, d.label]));
  const pairText = adjacentPairs
    .map((p) => {
      const a = labelByKey.get(p.a) ?? p.a;
      const b = labelByKey.get(p.b) ?? p.b;
      return `${a} → ${b}`;
    })
    .join(", ");
  const allDays = days.map((d) => d.label).join(", ");
  const wrapNote = cyclic
    ? " (the cycle repeats, so the last day is adjacent to the first)"
    : "";
  return (
    `${muscle} is trained on adjacent days: ${pairText}${wrapNote}. ` +
    `It recurs on: ${allDays}. ` +
    `The coach should judge whether the rest between these days is enough for this athlete.`
  );
};

// Detect, per muscle, every pair of CONSECUTIVE occurrences at distance 1
// (the adjacency the coach asked to be warned about), reporting the full
// recurrence and the minimum gap so they can judge "a prescindere della
// distanza". Returns one warning per muscle that has at least one adjacent
// pair, sorted by muscle slug for deterministic output.
export const computeMuscleAdjacency = (
  days: ReadonlyArray<AdjacencyDay>,
  opts: AdjacencyOptions,
): MuscleAdjacencyWarning[] => {
  const byMuscle = new Map<CatalogMuscleSlug, AdjacencyOccurrence[]>();
  for (const day of days) {
    for (const muscle of day.muscles) {
      const list = byMuscle.get(muscle) ?? [];
      list.push({ key: day.key, label: day.label, position: day.position });
      byMuscle.set(muscle, list);
    }
  }

  const cycleLength = opts.cyclic
    ? (opts.cycleLength ?? days.length)
    : undefined;
  const warnings: MuscleAdjacencyWarning[] = [];

  for (const [muscle, occ] of byMuscle) {
    if (occ.length < 2) continue;
    const sorted = [...occ].sort((a, b) => a.position - b.position);
    const gaps: AdjacencyPair[] = [];
    for (let i = 0; i + 1 < sorted.length; i++) {
      const a = sorted[i]!;
      const b = sorted[i + 1]!;
      const distance = b.position - a.position;
      // distance 0 = same calendar day (two exercises / two logged
      // workouts on one day) — not an adjacency problem, skip.
      if (distance > 0) gaps.push({ a: a.key, b: b.key, distance });
    }
    if (cycleLength !== undefined) {
      const last = sorted[sorted.length - 1]!;
      const first = sorted[0]!;
      const distance = cycleLength - last.position + first.position;
      if (distance > 0) gaps.push({ a: last.key, b: first.key, distance });
    }
    if (gaps.length === 0) continue;
    const minDistance = Math.min(...gaps.map((g) => g.distance));
    const adjacentPairs = gaps.filter((g) => g.distance === 1);
    if (adjacentPairs.length === 0) continue; // trigger = adjacency (Δ=1)
    warnings.push({
      muscleSlug: muscle,
      days: sorted,
      minDistance,
      adjacentPairs,
      message: buildMessage(muscle, sorted, adjacentPairs, opts.cyclic),
    });
  }

  warnings.sort((a, b) => a.muscleSlug.localeCompare(b.muscleSlug));
  return warnings;
};
